// Global Types
import type {
  $WithRouter,
} from '@vroskus/library-navigation';
import type {
  $Model,
  $ModelUpsertParams,
} from 'common.data';
import type {
  $Translate,
} from 'common.language';

// Global Enums
import {
  adminPages,
  adminSections,
} from 'common.enums';

// Global Components
import * as React from 'react';
import {
  Button,
  Card,
  Table,
} from 'reactstrap';
import {
  DeleteDialog,
  FormDialog,
  Loader,
  SectionHeader,
} from '@vroskus/library-components';

// Helpers
import {
  cleanFormValues,
} from '@vroskus/library-helpers/dist/browser';
import {
  formatDateTime,
} from 'common.helpers/dist/browser';
import {
  defaultDeleteItem,
  defaultGetData,
  defaultUpsertItem,
} from '../../../helpers';

// Components
import Page from '../../layout/Page';
import EmptyTable from '../../common/EmptyTable';
import RegionSeasonFields from '../../fields/RegionSeasonFields';
import ThQty from '../../common/ThQty';

// Services
import Services from '../../../services';

// Types
import type {
  $RedirectAction,
} from '../../../redux/modules/main/actions';

export type $OwnProps = $Translate & $WithRouter;

export type $StateProps = Record<string, never>;

export type $DispatchProps = {
  redirect: $RedirectAction;
};

type $Props = $DispatchProps & $OwnProps & $StateProps;

type $State = {
  data: Array<$Model<'RegionSeason'>>;
  loading: boolean;
  mainItem: $Model<'Region'> | null;
};

const pageId = adminPages.adminRegionSeasonsPage;

class AdminRegionSeasonsPageView extends React.Component<$Props, $State> {
  constructor(props: $Props) {
    super(props);

    this.state = {
      data: [],
      loading: false,
      mainItem: null,
    };
  }

  componentDidMount(): void {
    this.getData();
    this.setupSocket();
  }

  setupSocket(): void {
    Services.Socket.on({
      action: (data) => {
        if (data.modelName === 'RegionSeason') {
          this.getData();
        }
      },
      type: Services.Socket.type.modelChangedEvent,
    });
  }

  setStateAsync(state: Partial<$State>): Promise<void> {
    return new Promise((resolve) => {
      this.setState(
        (pervState: $State) => ({
          ...pervState,
          ...state,
        }),
        resolve,
      );
    });
  }

  async getData(): Promise<void> {
    const {
      params,
    } = this.props;

    const {
      RegionId,
    } = params;

    await defaultGetData({
      action: async () => {
        if (RegionId) {
          await this.setStateAsync({
            loading: true,
          });

          const mainItem = await Services.Api.getOneAdminRegion({
            id: RegionId,
          });
          const data = await Services.Api.getManyAdminRegionSeason({
            RegionId,
          });

          await this.setStateAsync({
            data,
            loading: false,
            mainItem,
          });
        }
      },
      errorAction: async () => this.setStateAsync({
        loading: false,
      }),
    });
  }

  renderHeader() {
    const {
      redirect,
      t,
    } = this.props;
    const {
      mainItem,
    } = this.state;

    if (mainItem !== null) {
      const RegionId = mainItem.id;

      const newItem: $ModelUpsertParams<'RegionSeason'> = {
        EndDate: new Date(),
        Name: '',
        RegionId,
        StartDate: new Date(),
      };

      return (
        <Card id={adminSections[pageId].header}>
          <SectionHeader
            description={mainItem !== null ? mainItem.Name : ''}
            rightButtons={(
              <FormDialog
                fields={(
                  <RegionSeasonFields item={newItem} />
                )}
                id={'create-region-season-dialog'}
                labelCloseButton={t('Close')}
                labelSaveButton={t('Save')}
                onAction={(values) => defaultUpsertItem({
                  action: async () => {
                    const params = cleanFormValues<$Model<'RegionSeason'>>({
                      ...newItem,
                      ...values,
                    });

                    await Services.Api.upsertAdminRegionSeason(params);
                  },
                })}
                subTitle={t('New')}
                title={t('Region season')}
              >
                <Button
                  color={'info'}
                  id={'create-region-season-button'}
                  size={'sm'}
                  title={t('Add new region season')}
                >
                  {t('Add new')}
                </Button>
              </FormDialog>
            )}
            title={t('Region seasons')}
            titleButton={(
              <Button
                className={'mr-2'}
                color={'secondary'}
                id={'redirect-admin-regions-page-button'}
                onClick={() => redirect({
                  pathname: '/admin/regions',
                })}
                size={'sm'}
              >
                <em className={'fa icon-arrow-left'} />
              </Button>
            )}
          />
        </Card>
      );
    }

    return null;
  }

  renderButtons(dataItem: $Model<'RegionSeason'>) {
    const {
      t,
    } = this.props;

    const {
      id,
    } = dataItem;

    return (
      <div className={'d-flex justify-content-end'}>
        <FormDialog
          fields={(
            <RegionSeasonFields item={dataItem} />
          )}
          id={`update-region-season-dialog-${id}`}
          labelCloseButton={t('Close')}
          labelSaveButton={t('Save')}
          onAction={(values) => defaultUpsertItem({
            action: async () => {
              const params = cleanFormValues<$Model<'RegionSeason'>>({
                ...dataItem,
                ...values,
              });

              await Services.Api.upsertAdminRegionSeason(params);
            },
          })}
          subTitle={dataItem.Name}
          title={t('Regio seasons')}
        >
          <Button
            className={'mr-1'}
            color={'warning'}
            id={`update-region-season-button-${id}`}
            outline
            size={'sm'}
          >
            {t('Edit')}
          </Button>
        </FormDialog>
        <DeleteDialog
          content={t('Do you really want to delete?')}
          id={`delete-region-season-dialog-${id}`}
          labelCloseButton={t('Close')}
          labelDeleteButton={t('Delete')}
          onAction={() => defaultDeleteItem({
            action: async () => Services.Api.deleteAdminRegionSeason(dataItem),
          })}
          subTitle={dataItem.Name}
          title={t('Region season')}
        >
          <Button
            color={'danger'}
            id={`delete-region-season-button-${id}`}
            outline
            size={'sm'}
          >
            {t('Delete')}
          </Button>
        </DeleteDialog>
      </div>
    );
  }

  renderTable() {
    const {
      t,
    } = this.props;
    const {
      data,
      mainItem,
    } = this.state;

    return data.length === 0 ? (
      <EmptyTable className={'mt-5'} />
    ) : (
      <Table responsive>
        <thead>
          <tr>
            <th>{t('Name')}</th>
            <th>{t('Start date')}</th>
            <th>{t('End date')}</th>
            <ThQty value={data.length} />
          </tr>
        </thead>
        <tbody>
          {data.map((dataItem: $Model<'RegionSeason'>) => (
            <tr key={dataItem.id}>
              <td>{dataItem.Name}</td>
              <td>
                {mainItem !== null && formatDateTime({
                  dateFormat: mainItem.DateFormat,
                  timeFormat: mainItem.TimeFormat,
                  value: dataItem.StartDate,
                })}
              </td>
              <td>
                {mainItem !== null && formatDateTime({
                  dateFormat: mainItem.DateFormat,
                  timeFormat: mainItem.TimeFormat,
                  value: dataItem.EndDate,
                })}
              </td>
              <td>
                {this.renderButtons(dataItem)}
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    );
  }

  renderContent() {
    const {
      loading,
    } = this.state;

    return (
      <Card id={adminSections[pageId].list}>
        <Loader
          loading={loading === true}
          overlay
        >
          {this.renderTable()}
        </Loader>
      </Card>
    );
  }

  render() {
    return (
      <Page id={pageId}>
        <>
          {this.renderHeader()}
          {this.renderContent()}
        </>
      </Page>
    );
  }
}

export default AdminRegionSeasonsPageView;
